import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { PreviewCanvas } from "../components/graderoom/PreviewCanvas";
import { NodeStack } from "../components/graderoom/NodeStack";
import { NodeControls } from "../components/graderoom/NodeControls";
import { Scopes } from "../components/graderoom/Scopes";
import { newNode } from "../components/graderoom/gradeMath";
import { SHOT_PRESETS } from "../components/graderoom/presets";

const API = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

const CHIP = {
  ungraded: "bg-white/10 text-white/50",
  draft: "bg-amber-500/20 text-amber-300",
  rendered: "bg-emerald-500/20 text-emerald-300",
  queued: "bg-sky-500/20 text-sky-300",
  rendering: "bg-sky-500/30 text-sky-200 animate-pulse",
  failed: "bg-red-500/25 text-red-300",
  stalled: "bg-yellow-500/25 text-yellow-300",
};

// Live-poll cap per Rohit 2026-06-13: stop after 10 min (or terminal state),
// show "stalled" rather than polling forever; retry is manual only.
const RENDER_POLL_INTERVAL_MS = 5000;
const RENDER_POLL_CAP_MS = 10 * 60 * 1000;

/** GRADE ROOM — Stage 14.5 grading module (P1 scaffold).
 *  Flow (spec §1): Project picker → Clip grid → Workbench → Render/Propagate. */
export default function GradeRoomPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState("projects"); // projects | clips | workbench
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("");
  const [project, setProject] = useState(null);
  const [clips, setClips] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [clip, setClip] = useState(null);
  const [proxy, setProxy] = useState(null);
  const [nodes, setNodes] = useState([newNode("base")]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [bypass, setBypass] = useState(false);
  const [recipeId, setRecipeId] = useState(null);
  const [version, setVersion] = useState(0);
  const [pixels, setPixels] = useState(null);
  const [busy, setBusy] = useState("");
  const [matteIdx, setMatteIdx] = useState(-1);
  const [looks, setLooks] = useState([]);
  const [showLookDialog, setShowLookDialog] = useState(false);
  const [lookBrand, setLookBrand] = useState("");
  const [stills, setStills] = useState([]);
  // Live render-poll state: per-shot { startedPollingAt, stalled }
  const [renderPoll, setRenderPoll] = useState({});  // shot_id → meta
  const renderPollRef = React.useRef({});            // sync mirror for the loop
  const inflight = React.useRef(false); // guards double-click duplicate POSTs
  const captureFn = React.useRef(null);

  const loadProjects = async () => {
    try {
      const { data } = await axios.get(
        `${API}/api/grade-room/projects`, authHeaders());
      setProjects(data.projects || []);
    } catch {
      toast.error("Could not load projects");
    }
  };

  useEffect(() => {
    axios.get(`${API}/api/grade-room/projects`, authHeaders())
      .then(({ data }) => setProjects(data.projects || []))
      .catch(() => toast.error("Could not load projects"));
    axios.get(`${API}/api/grade-room/recipes?scope=look_bible`, authHeaders())
      .then(({ data }) => setLooks(data.recipes || []))
      .catch(() => {});
  }, []);

  // Lazy drift-score measurement triggered from the picker. Fires the
  // analysis-only `/auto-balance/preview` endpoint — never `/apply`, so a
  // picker click can't silently rewrite recipes.
  const measureDrift = async (projectId) => {
    if (inflight.current) return;
    inflight.current = true;
    setBusy(`measure-${projectId}`);
    try {
      const { data } = await axios.post(
        `${API}/api/projects/${projectId}/grades/auto-balance/preview`,
        { target: "median" }, authHeaders());
      if (data.clips_analyzed) {
        toast.success(`Drift measured · ${data.clips_analyzed} clip(s)`);
        await loadProjects();
      } else {
        toast.error("No hero proxies cached — open the project first");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Drift measure failed");
    }
    inflight.current = false;
    setBusy("");
  };

  const uploadLut = useCallback(async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/api/grade-room/luts`, fd, authHeaders());
      toast.success(`LUT uploaded · ${data.name}`);
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "LUT upload failed");
      throw e;
    }
  }, []);

  const saveLook = async () => {
    if (!lookBrand.trim()) { toast.error("Brand tag required for Look Bible"); return; }
    if (inflight.current) return;
    inflight.current = true;
    try {
      const { data } = await axios.post(`${API}/api/grade-room/recipes`, {
        name: `${lookBrand} look`, scope: "look_bible", brand_tag: lookBrand.trim(), nodes,
      }, authHeaders());
      setLooks([data, ...looks]);
      setShowLookDialog(false); setLookBrand("");
      toast.success(`Saved to Look Bible · ${data.brand_tag}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Look save failed");
    }
    inflight.current = false;
  };

  const applyLook = (lookId) => {
    const look = looks.find((l) => l.id === lookId);
    if (!look) return;
    setNodes(look.nodes?.length ? look.nodes : nodes);
    setActiveIdx(0);
    toast.success(`Look applied · ${look.name} (save to keep it on this clip)`);
  };

  // ── Auto shot-balance (P2.1) ──────────────────────────────────────────
  const [balancePreview, setBalancePreview] = useState(null); // {target_lab, per_clip, …}
  const [balanceMode, setBalanceMode] = useState("median");   // "median" | hero_clip:<id>

  const runBalancePreview = async (mode) => {
    if (inflight.current) return;
    inflight.current = true;
    setBusy("balance-preview");
    try {
      const { data } = await axios.post(
        `${API}/api/projects/${project.id}/grades/auto-balance/preview`,
        { target: mode }, authHeaders());
      setBalancePreview(data);
      const skipped = (data.stats_missing || []).length;
      toast.success(`Auto-balance preview · ${data.clips_analyzed} clip(s) analyzed`
        + (skipped ? ` · ${skipped} skipped (no proxy)` : ""));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Balance preview failed");
    }
    inflight.current = false;
    setBusy("");
  };

  const applyBalance = async () => {
    if (inflight.current || !balancePreview) return;
    inflight.current = true;
    setBusy("balance-apply");
    try {
      const { data } = await axios.post(
        `${API}/api/projects/${project.id}/grades/auto-balance/apply`,
        { target: balanceMode, queue_renders: false }, authHeaders());
      toast.success(`Auto-balance applied · ${data.applied} clip(s) · `
        + `${data.skipped_drafts} draft(s) untouched`);
      setBalancePreview(null);
      await loadClips(project.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message
        || e?.response?.data?.detail || "Balance apply failed");
    }
    inflight.current = false;
    setBusy("");
  };

  const applyLookToUngraded = async (look) => {
    if (inflight.current) return;
    inflight.current = true;
    setBusy("apply-look");
    try {
      const { data } = await axios.post(
        `${API}/api/projects/${project.id}/grades/apply-look`,
        { look_recipe_id: look.id, queue_renders: true }, authHeaders());
      toast.success(
        `${look.brand_tag} look applied to ${data.applied} clip(s) · ${data.queued} renders queued · ${data.skipped_drafts} draft(s) untouched`);
      // The polling tick seeds the 10-min clock automatically on the next
      // run (≤5s after `loadClips` populates the grid with `render_status`).
      await loadClips(project.id);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d?.message || d || "Apply look failed");
    }
    inflight.current = false;
    setBusy("");
  };

  const grabStill = async () => {
    if (!captureFn.current) return;
    const blob = await captureFn.current();
    if (!blob) { toast.error("Nothing to capture yet"); return; }
    const fd = new FormData();
    fd.append("project_id", project.id);
    fd.append("shot_id", clip.shot_id);
    fd.append("label", clip.label);
    fd.append("nodes_json", JSON.stringify(nodes));
    fd.append("file", blob, "still.jpg");
    try {
      const { data } = await axios.post(`${API}/api/grade-room/stills`, fd, authHeaders());
      setStills([data, ...stills]);
      toast.success(`Still grabbed · ${data.label}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Still capture failed");
    }
  };

  const deleteStill = async (id) => {
    try {
      await axios.delete(`${API}/api/grade-room/stills/${id}`, authHeaders());
      setStills(stills.filter((s) => s.id !== id));
    } catch { toast.error("Delete failed"); }
  };

  const loadClips = useCallback(async (pid) => {
    const { data } = await axios.get(
      `${API}/api/projects/${pid}/clips/gradeable`, authHeaders());
    setClips(data.clips || []);
  }, []);

  // (The polling tick below seeds the per-shot 10-min clock on first sight
  // of a queued/rendering job, so a dedicated seed-effect isn't needed.)

  // ── Live render-poll on the clip grid ─────────────────────────────────
  // Polls /grade-room/render-jobs every 5s while any clip is queued or
  // rendering. Hard-caps each clip at 10 min: after that we flag it
  // "stalled" and stop polling that shot (manual retry only).
  useEffect(() => {
    if (step !== "clips" || !project?.id) return undefined;
    let alive = true;

    const tick = async () => {
      if (!alive) return;
      try {
        const { data } = await axios.get(
          `${API}/api/projects/${project.id}/grade-room/render-jobs`,
          authHeaders());
        if (!alive) return;
        const jobsByShot = Object.fromEntries(
          (data.jobs || []).map((j) => [j.shot_id, j]));

        const now = Date.now();
        const currMeta = renderPollRef.current;
        const nextMeta = { ...currMeta };
        let metaChanged = false;

        setClips((prev) => prev.map((c) => {
          const j = jobsByShot[c.shot_id];
          if (!j) return c;
          const updated = { ...c };
          // Terminal: render done → flip the chip + swap thumbnail to graded
          if (j.status === "done") {
            updated.grade_status = "rendered";
            updated.render_status = null;
            updated.render_job_id = j.id;
            updated.render_progress_pct = 100;
            updated.graded_video_url = j.graded_video_url || j.output_url || c.graded_video_url;
            if (nextMeta[c.shot_id]) { delete nextMeta[c.shot_id]; metaChanged = true; }
          } else if (j.status === "failed") {
            updated.render_status = "failed";
            updated.render_job_id = j.id;
            updated.render_error = j.error || "Render failed";
            if (nextMeta[c.shot_id]) { delete nextMeta[c.shot_id]; metaChanged = true; }
          } else {
            // queued / rendering — keep polling, update progress
            updated.render_status = j.status;
            updated.render_job_id = j.id;
            updated.render_progress_pct = j.progress_pct || 0;
            const meta = currMeta[c.shot_id]
              || { startedPollingAt: now, stalled: false };
            // 10-min hard cap from when WE started watching this shot.
            if (now - meta.startedPollingAt >= RENDER_POLL_CAP_MS) {
              updated.render_status = "stalled";
              if (!meta.stalled) {
                nextMeta[c.shot_id] = { ...meta, stalled: true };
                metaChanged = true;
              }
            } else if (!currMeta[c.shot_id]) {
              nextMeta[c.shot_id] = meta;
              metaChanged = true;
            }
          }
          return updated;
        }));

        if (metaChanged) setRenderPoll(nextMeta);
      } catch (e) {
        // Network blip — keep polling, don't surface to user.
      }
    };

    // First tick is immediate, then on interval.
    tick();
    const id = setInterval(tick, RENDER_POLL_INTERVAL_MS);
    return () => { alive = false; clearInterval(id); };
  }, [step, project?.id]);

  // Keep ref in sync with state so the polling tick can read latest meta
  // without including `renderPoll` in its dep array (which would tear down
  // the interval on every tick).
  useEffect(() => { renderPollRef.current = renderPoll; }, [renderPoll]);

  const retryRender = async (jobId, shotId) => {
    if (!jobId) return;
    try {
      await axios.post(
        `${API}/api/grade-room/render-jobs/${jobId}/retry`, {}, authHeaders());
      toast.success("Retry queued");
      // Reset the 10-min clock for this shot and resume polling.
      setRenderPoll((curr) => ({
        ...curr, [shotId]: { startedPollingAt: Date.now(), stalled: false },
      }));
      // Optimistic chip update
      setClips((prev) => prev.map((c) => c.shot_id === shotId
        ? { ...c, render_status: "queued", render_error: null, render_progress_pct: 0 }
        : c));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry failed");
    }
  };

  const openProject = async (p) => {
    setProject(p); setBusy("clips");
    try {
      await loadClips(p.id);
      setSelected(new Set());
      setStep("clips");
      axios.get(`${API}/api/grade-room/stills?project_id=${p.id}`, authHeaders())
        .then(({ data }) => setStills(data.stills || [])).catch(() => {});
    } catch { toast.error("Could not load clips"); }
    setBusy("");
  };

  const openWorkbench = async (c) => {
    setClip(c); setBusy("proxy"); setStep("workbench");
    setProxy(null); setRecipeId(null); setVersion(0);
    try {
      // Existing draft? Load latest version of the clip's grade.
      const { data: rec } = await axios.get(
        `${API}/api/grade-room/recipes?project_id=${project.id}&shot_id=${c.shot_id}&scope=clip`,
        authHeaders());
      const latest = (rec.recipes || [])[0];
      if (latest) {
        setNodes(latest.nodes?.length ? latest.nodes : [newNode("base")]);
        setRecipeId(latest.id); setVersion(latest.version || 1);
      } else {
        setNodes([newNode("base")]);
      }
      setActiveIdx(0);
      const { data } = await axios.post(
        `${API}/api/projects/${project.id}/clips/${c.shot_id}/proxy-frames`,
        {}, authHeaders());
      setProxy(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Proxy extraction failed");
    }
    setBusy("");
  };

  const saveGrade = useCallback(async () => {
    if (inflight.current) return;
    inflight.current = true;
    setBusy("save");
    try {
      const body = {
        name: `${clip.label} grade`, scope: "clip",
        project_id: project.id, shot_id: clip.shot_id, nodes,
      };
      const { data } = recipeId
        ? await axios.put(`${API}/api/grade-room/recipes/${recipeId}`, body, authHeaders())
        : await axios.post(`${API}/api/grade-room/recipes`, body, authHeaders());
      setRecipeId(data.id); setVersion(data.version);
      toast.success(`Grade saved · R${data.version}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    inflight.current = false;
    setBusy("");
  }, [clip, project, nodes, recipeId]);

  const renderClip = async () => {
    if (!recipeId) { toast.error("Save the grade first"); return; }
    if (inflight.current) return;
    inflight.current = true;
    setBusy("render");
    try {
      const { data } = await axios.post(
        `${API}/api/projects/${project.id}/clips/${clip.shot_id}/render-grade`,
        { recipe_id: recipeId }, authHeaders());
      toast.success(`Render queued · ${data.passes} passes · parity ${data.parity_hash.slice(0, 8)}`);
      // Start the 10-min poll clock for this shot from NOW.
      setRenderPoll((curr) => ({
        ...curr,
        [clip.shot_id]: { startedPollingAt: Date.now(), stalled: false },
      }));
      // Reflect on the in-memory clip array so the chip flips immediately
      // when the user returns to the grid.
      setClips((prev) => prev.map((c) => c.shot_id === clip.shot_id
        ? { ...c, render_status: "queued", render_job_id: data.job_id, render_progress_pct: 0 }
        : c));
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d?.message || d || "Render queue failed");
    }
    inflight.current = false;
    setBusy("");
  };

  const propagate = async (target) => {
    if (!recipeId) { toast.error("Save the grade first"); return; }
    setBusy("propagate");
    try {
      const { data } = await axios.post(
        `${API}/api/projects/${project.id}/grades/propagate`,
        { source_shot_id: clip.shot_id, target }, authHeaders());
      toast.success(`Grade copied to ${data.propagated} clip(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Propagate failed");
    }
    setBusy("");
  };

  const filtered = useMemo(
    () => projects.filter((p) =>
      !filter || (p.name || "").toLowerCase().includes(filter.toLowerCase())
      || (p.brand_tag || "").toLowerCase().includes(filter.toLowerCase())),
    [projects, filter]);

  return (
    <div className="min-h-screen bg-[#0c0a09] text-white/90 px-6 py-6" data-testid="grade-room-page">
      <header className="flex items-center gap-4 pb-5 border-b border-white/10 mb-6">
        <button onClick={() => step === "projects" ? navigate("/recipes") : setStep(step === "workbench" ? "clips" : "projects")}
          className="text-white/40 hover:text-orange-300 text-sm" data-testid="grade-back-btn">← Back</button>
        <div>
          <p className="font-mono text-[10px] tracking-[0.2em] text-orange-400">STAGE 14.5 · COLOR</p>
          <h1 className="text-xl font-extrabold tracking-tight">GRADE ROOM</h1>
        </div>
        {project && <span className="text-sm text-white/40">· {project.name}</span>}
        {clip && step === "workbench" && (
          <span className="font-mono text-xs text-white/50">· {clip.label} {version > 0 && `· R${version}`}</span>
        )}
        <span className="ml-auto font-mono text-[10px] text-white/30 tracking-widest">P1 · CDL+LUT WORKER CONTRACT v1</span>
      </header>

      {step === "projects" && (
        <div className="max-w-3xl" data-testid="grade-project-picker">
          <input placeholder="Filter by name or brand…" value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-full mb-4 bg-white/[0.04] border border-white/15 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-orange-500"
            data-testid="grade-project-filter" />
          <div className="space-y-2">
            {filtered.map((p) => {
              const ds = p.drift_score || { status: "not_measured" };
              const isMeasured = ds.status === "measured";
              return (
              <div key={p.id} className="flex items-center gap-4 px-4 py-3 rounded-xl bg-white/[0.03] border border-white/10">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">{p.name}</p>
                  <p className="text-xs text-white/40">
                    {p.clips_done}/{p.shots_total} clips done {p.brand_tag ? `· ${p.brand_tag}` : ""}
                  </p>
                  <p className="text-[10px] font-mono mt-0.5"
                    data-testid={`drift-score-${p.id}`}>
                    {isMeasured ? (
                      <span className={ds.median_de > 4
                        ? "text-amber-400"
                        : ds.median_de > 2
                          ? "text-cyan-300"
                          : "text-emerald-300"}>
                        drift · median ΔE {ds.median_de} · max {ds.max_de}
                        <span className="text-white/30"> · {ds.clips_measured} clip(s) · {ds.target_mode}</span>
                      </span>
                    ) : (
                      <span className="text-white/30">drift · not measured</span>
                    )}
                  </p>
                </div>
                {!isMeasured && p.gradeable && (
                  <button onClick={() => measureDrift(p.id)}
                    disabled={busy === `measure-${p.id}`}
                    data-testid={`measure-drift-${p.id}`}
                    title="Run hero-frame Lab analysis on every clip (no recipes written)"
                    className="px-2.5 py-1 rounded-lg text-[10px] font-mono uppercase tracking-wider border border-cyan-500/40 text-cyan-200 hover:bg-cyan-500/10 disabled:opacity-40">
                    {busy === `measure-${p.id}` ? "…" : "Measure"}
                  </button>
                )}
                <button disabled={!p.gradeable || busy === "clips"}
                  onClick={() => openProject(p)}
                  data-testid={`grade-open-project-${p.id}`}
                  className="px-4 py-1.5 rounded-lg text-sm font-bold bg-orange-500 text-black hover:bg-orange-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                  Grade
                </button>
              </div>
              );
            })}
            {filtered.length === 0 && <p className="text-sm text-white/30 italic">No projects.</p>}
          </div>
        </div>
      )}

      {step === "clips" && (
        <div data-testid="grade-clips-view">
          {/* P2.1 Auto shot-balance banner (always available; sits ABOVE
              brand-look banner because shot-balance is the foundation that
              kills AI drift; brand-look is the artistic layer on top). */}
          <div className="flex flex-wrap items-center gap-3 mb-4 px-4 py-3 rounded-xl bg-cyan-500/10 border border-cyan-500/30"
            data-testid="grade-auto-balance-banner">
            <div className="flex-1 min-w-[260px] text-sm">
              <span className="font-bold text-cyan-300">Auto shot-balance</span>
              <span className="text-white/60"> — normalize all clips&apos; exposure + WB to a common baseline (kills AI shot drift).</span>
            </div>
            <select value={balanceMode}
              onChange={(e) => setBalanceMode(e.target.value)}
              data-testid="grade-balance-mode-select"
              className="px-2 py-1.5 rounded-lg text-xs bg-black/40 border border-white/15 text-white/80 outline-none focus:border-cyan-500">
              <option value="median">Target: median of all clips</option>
              {clips.map((c) => (
                <option key={c.shot_id} value={`hero_clip:${c.shot_id}`}>
                  Target: hero clip · {c.label}
                </option>
              ))}
            </select>
            <button onClick={() => runBalancePreview(balanceMode)}
              disabled={busy === "balance-preview"}
              data-testid="grade-balance-preview-btn"
              className="px-3 py-1.5 rounded-lg text-xs font-bold border border-cyan-500/60 text-cyan-200 hover:bg-cyan-500/10 disabled:opacity-40">
              {busy === "balance-preview" ? "Analyzing…" : "Preview"}
            </button>
            {balancePreview && (
              <button onClick={applyBalance}
                disabled={busy === "balance-apply"}
                data-testid="grade-balance-apply-btn"
                className="px-3 py-1.5 rounded-lg text-xs font-bold bg-cyan-500 text-black hover:bg-cyan-400 disabled:opacity-40">
                {busy === "balance-apply" ? "Applying…" : `Apply to ${
                  Object.keys(balancePreview.per_clip || {}).length} clip(s)`}
              </button>
            )}
            {balancePreview && (
              <span className="font-mono text-[10px] text-cyan-200/70"
                data-testid="grade-balance-target-lab">
                target L*a*b* = [{balancePreview.target_lab?.join(", ")}]
                {balancePreview.mode === "hero_clip"
                  ? " · hero" : " · median"}
              </span>
            )}
          </div>

          {(() => {
            const ungraded = clips.filter((c) => c.grade_status === "ungraded");
            const drafts = clips.filter((c) => c.grade_status === "draft");
            const brandLook = looks.find((l) => l.brand_tag && l.brand_tag === project?.brand_tag) || looks[0];
            if (!brandLook || ungraded.length === 0) return null;
            return (
              <div className="flex items-center gap-3 mb-4 px-4 py-3 rounded-xl bg-amber-500/10 border border-amber-500/30"
                data-testid="grade-brand-look-banner">
                <div className="flex-1 text-sm">
                  <span className="font-bold text-amber-300">Apply {brandLook.brand_tag || brandLook.name} look</span>
                  <span className="text-white/60"> to {ungraded.length} ungraded clip(s) — queues {ungraded.length} render(s)</span>
                  {drafts.length > 0 && (
                    <span className="text-white/40"> · {drafts.length} draft grade(s) will be left untouched</span>
                  )}
                </div>
                <button onClick={() => applyLookToUngraded(brandLook)}
                  disabled={busy === "apply-look"}
                  data-testid="grade-brand-look-apply-btn"
                  className="px-4 py-1.5 rounded-lg text-xs font-bold bg-amber-500 text-black hover:bg-amber-400 disabled:opacity-40">
                  {busy === "apply-look" ? "Applying…" : `Apply to ${ungraded.length}`}
                </button>
              </div>
            );
          })()}
          <div className="flex items-center gap-3 mb-4">
            <p className="text-sm text-white/50">{clips.length} clip(s)</p>
            <button onClick={() => setSelected(new Set(clips.map((c) => c.shot_id)))}
              className="text-xs text-white/40 hover:text-orange-300">Select all</button>
            {selected.size > 0 && <span className="text-xs text-orange-300 font-mono">{selected.size} selected</span>}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {clips.map((c) => {
              // Live render-poll surface: queued/rendering/stalled/failed
              // override the static draft/rendered/ungraded chip until terminal.
              const liveStatus = c.render_status;       // queued|rendering|stalled|failed|null
              const chipKey = liveStatus || c.grade_status;
              const chipLabel = liveStatus === "queued" ? "queued"
                : liveStatus === "rendering" ? `rendering · ${c.render_progress_pct ?? 0}%`
                : liveStatus === "stalled" ? "render stalled"
                : liveStatus === "failed" ? "render failed"
                : c.grade_status;
              const showRetry = liveStatus === "failed" || liveStatus === "stalled";
              return (
              <div key={c.shot_id}
                data-testid={`grade-clip-${c.shot_id}`}
                className={`rounded-xl overflow-hidden border cursor-pointer transition-colors ${selected.has(c.shot_id) ? "border-orange-500" : "border-white/10 hover:border-white/30"}`}>
                <div className="relative aspect-video bg-black" onClick={() => openWorkbench(c)}>
                  {c.poster_url && <img src={c.poster_url} alt={c.label} className="w-full h-full object-cover" />}
                  <span className="absolute bottom-1 left-1 font-mono text-[10px] bg-black/70 px-1.5 py-0.5 rounded">{c.label} · {c.duration}s</span>
                  {liveStatus === "rendering" && (
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/60"
                      data-testid={`render-progress-bar-${c.shot_id}`}>
                      <div className="h-full bg-sky-400 transition-all duration-500"
                           style={{ width: `${Math.max(5, c.render_progress_pct || 0)}%` }} />
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between px-2 py-1.5 bg-white/[0.03] gap-1">
                  <span className={`text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded ${CHIP[chipKey] || CHIP.ungraded}`}
                    data-testid={`grade-chip-${c.shot_id}`}
                    title={liveStatus === "failed" ? (c.render_error || "Render failed") : undefined}>
                    {chipLabel}
                  </span>
                  {showRetry ? (
                    <button onClick={(e) => { e.stopPropagation(); retryRender(c.render_job_id, c.shot_id); }}
                      className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-orange-500/80 text-black hover:bg-orange-400"
                      data-testid={`render-retry-${c.shot_id}`}
                      title={liveStatus === "stalled"
                        ? "Polling stopped after 10 min — click to retry"
                        : (c.render_error || "Render failed")}>
                      Retry
                    </button>
                  ) : (
                    <input type="checkbox" checked={selected.has(c.shot_id)}
                      onChange={(e) => {
                        const next = new Set(selected);
                        e.target.checked ? next.add(c.shot_id) : next.delete(c.shot_id);
                        setSelected(next);
                      }}
                      className="accent-orange-500" data-testid={`clip-select-${c.shot_id}`} />
                  )}
                </div>
              </div>
              );
            })}
          </div>
        </div>
      )}

      {step === "workbench" && (
        <div className="grid grid-cols-12 gap-5" data-testid="grade-workbench">
          <aside className="col-span-3 xl:col-span-2">
            <NodeStack nodes={nodes} activeIdx={activeIdx} onChange={setNodes} onSelect={setActiveIdx} />
          </aside>
          <main className="col-span-6 xl:col-span-7 space-y-4">
            {busy === "proxy" && <p className="text-sm text-white/40 animate-pulse">Extracting proxy frames…</p>}
            {proxy && (
              <PreviewCanvas frames={proxy.frames} heroUrl={proxy.hero_url}
                nodes={nodes} bypass={bypass} matteIdx={matteIdx} onPixels={setPixels}
                onRegisterCapture={(fn) => { captureFn.current = fn; }} />
            )}
            <div className="flex flex-wrap items-center gap-2">
              <button onClick={() => setBypass(!bypass)}
                data-testid="grade-bypass-btn"
                className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors ${bypass ? "border-orange-500 text-orange-300" : "border-white/15 text-white/50 hover:text-white"}`}>
                {bypass ? "BYPASSED (A)" : "GRADE ON (B)"}
              </button>
              <button onClick={saveGrade} disabled={busy === "save"}
                data-testid="grade-save-btn"
                className="px-4 py-1.5 rounded-lg text-xs font-bold bg-orange-500 text-black hover:bg-orange-400 disabled:opacity-40">
                {recipeId ? `Save as R${version + 1}` : "Save Grade"}
              </button>
              <button onClick={renderClip} disabled={busy === "render" || !recipeId}
                data-testid="grade-render-btn"
                className="px-4 py-1.5 rounded-lg text-xs font-bold bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40">
                Render this clip
              </button>
              <button onClick={() => propagate([...selected])} disabled={!selected.size || !recipeId}
                data-testid="grade-propagate-selected-btn"
                className="px-3 py-1.5 rounded-lg text-xs border border-white/15 text-white/60 hover:text-white disabled:opacity-30">
                Copy to selected ({selected.size})
              </button>
              <button onClick={() => propagate("all")} disabled={!recipeId}
                data-testid="grade-propagate-all-btn"
                className="px-3 py-1.5 rounded-lg text-xs border border-white/15 text-white/60 hover:text-white disabled:opacity-30">
                Copy to all clips
              </button>
              <button onClick={() => setShowLookDialog(true)}
                data-testid="grade-save-look-btn"
                className="px-3 py-1.5 rounded-lg text-xs border border-amber-500/40 text-amber-300 hover:bg-amber-500/10">
                Save to Look Bible
              </button>
              <button onClick={grabStill}
                data-testid="grade-grab-still-btn"
                className="px-3 py-1.5 rounded-lg text-xs border border-white/15 text-white/60 hover:text-white">
                Grab Still
              </button>
              <select onChange={(e) => {
                  const p = SHOT_PRESETS[parseInt(e.target.value, 10)];
                  if (p) { setNodes([...nodes, p.node()]); setActiveIdx(nodes.length); }
                  e.target.value = "";
                }}
                defaultValue=""
                data-testid="grade-preset-select"
                className="px-2 py-1.5 rounded-lg text-xs bg-white/[0.04] border border-white/15 text-white/60 outline-none">
                <option value="" disabled>+ Preset…</option>
                {SHOT_PRESETS.map((p, i) => (
                  <option key={p.label} value={i}>{p.label}</option>
                ))}
              </select>
              {looks.length > 0 && (
                <select onChange={(e) => { if (e.target.value) applyLook(e.target.value); e.target.value = ""; }}
                  defaultValue=""
                  data-testid="grade-look-select"
                  className="px-2 py-1.5 rounded-lg text-xs bg-white/[0.04] border border-white/15 text-white/60 outline-none">
                  <option value="" disabled>Apply look…</option>
                  {looks.map((l) => (
                    <option key={l.id} value={l.id}>{l.brand_tag} · {l.name} (R{l.version})</option>
                  ))}
                </select>
              )}
            </div>
            {showLookDialog && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30"
                data-testid="grade-look-dialog">
                <input value={lookBrand} onChange={(e) => setLookBrand(e.target.value)}
                  placeholder="Brand tag (e.g. ZURA)"
                  className="flex-1 bg-black/40 border border-white/15 rounded-lg px-3 py-1.5 text-xs outline-none focus:border-amber-500"
                  data-testid="grade-look-brand-input" />
                <button onClick={saveLook} data-testid="grade-look-save-confirm"
                  className="px-3 py-1.5 rounded-lg text-xs font-bold bg-amber-500 text-black hover:bg-amber-400">Save</button>
                <button onClick={() => setShowLookDialog(false)}
                  className="px-2 py-1.5 text-xs text-white/40 hover:text-white">Cancel</button>
              </div>
            )}
            <Scopes pixels={pixels} />
            {stills.length > 0 && (
              <div data-testid="grade-stills-gallery">
                <p className="text-[10px] font-mono uppercase tracking-widest text-white/30 mb-2">
                  Stills · click to recall grade
                </p>
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {stills.map((s) => (
                    <div key={s.id} className="relative shrink-0 group"
                      data-testid={`grade-still-${s.id}`}>
                      <img src={`${API}${s.image_url}`} alt={s.label}
                        onClick={() => {
                          if (s.nodes?.length) { setNodes(s.nodes); setActiveIdx(0); toast.success(`Grade recalled from still · ${s.label}`); }
                        }}
                        className="h-16 rounded-md border border-white/10 cursor-pointer hover:border-orange-500 transition-colors" />
                      <span className="absolute bottom-0.5 left-1 font-mono text-[8px] bg-black/70 px-1 rounded">{s.label}</span>
                      <button onClick={() => deleteStill(s.id)}
                        className="absolute -top-1 -right-1 hidden group-hover:block w-4 h-4 rounded-full bg-red-500 text-white text-[9px] leading-4">✕</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </main>
          <aside className="col-span-3 max-h-[80vh] overflow-y-auto pr-1">
            <NodeControls node={nodes[activeIdx]}
              onChange={(n) => { const next = [...nodes]; next[activeIdx] = n; setNodes(next); }}
              matteOn={matteIdx === activeIdx}
              onMatteToggle={() => setMatteIdx(matteIdx === activeIdx ? -1 : activeIdx)}
              onLutUpload={uploadLut} />
          </aside>
        </div>
      )}
    </div>
  );
}
